<template>
  <ADrawer
    :width="drawerWidth"
    :open="open"
    class="events-calendar-filter-drawer"
    :body-style="{ paddingBottom: '24px' }"
    :footer-style="{ textAlign: 'center', padding: '16px 48px 32px 48px' }"
    @close="emit('update:open', false)"
  >
    <template #title>
      <div class="events-calendar-filter-drawer__header-shell">
        <div class="events-calendar-filter-drawer__header-overline">Фильтр</div>
      </div>
    </template>

    <template #closeIcon>
      <span class="events-calendar-filter-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <AForm layout="vertical" class="events-calendar-filter-drawer__form">
      <div class="events-calendar-filter-drawer__fields-wrapper">
        <AFormItem label="Формат мероприятия" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.activityFormat"
            :options="formatOptions"
            placeholder="Все форматы"
          />
        </AFormItem>

        <AFormItem label="Период проведения" class="stc-ant-field-skin">
          <AConfigProvider :locale="ruRU">
            <ARangePicker
              v-model:value="draft.dateRange"
              style="width: 100%"
              format="DD.MM.YYYY"
              :placeholder="['дд.мм.гггг', 'дд.мм.гггг']"
            >
              <template #separator>-</template>
              <template #suffixIcon>
                <img :src="`${ICONS_BASE}/range-picker-icon.svg`" alt="" />
              </template>
            </ARangePicker>
          </AConfigProvider>
        </AFormItem>
      </div>

      <div class="events-calendar-filter-drawer__fields-section">
        <AFormItem label="Тип мероприятия" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.activityType"
            :options="typeOptions"
            placeholder="Все типы"
          />
        </AFormItem>
      </div>
    </AForm>

    <template #footer>
      <div class="events-calendar-filter-drawer__footer-container">
        <button
          type="button"
          class="btn btn-outline btn-medium"
          :disabled="isResetDisabled"
          @click="handleReset"
        >
          Сбросить
        </button>
        <button
          type="button"
          class="btn btn-primary btn-medium"
          :disabled="isApplyDisabled"
          @click="handleApply"
        >
          Применить
        </button>
      </div>
    </template>
  </ADrawer>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import {
  Col as ACol,
  ConfigProvider as AConfigProvider,
  DatePicker as ADatePicker,
  Drawer as ADrawer,
  Form as AForm,
  FormItem as AFormItem,
  Row as ARow,
  Select as ASelect,
  Space as ASpace,
} from 'ant-design-vue';
import type { Dayjs } from 'dayjs';
import ruRU from 'ant-design-vue/es/locale/ru_RU';
import { submitNewsFilters } from '../../utils/news-filters';
import { EVENTS_CALENDAR_FILTER_SUBMIT_OPTIONS } from './events-calendar-filter-config';
import {
  applyEventsCalendarFilterDraft,
  draftsEqual,
  getEventsCalendarScope,
  getSelectOptions,
  readEventsCalendarFilterDraft,
  type EventsCalendarFilterDraft,
} from './sync-filters';

const ARangePicker = ADatePicker.RangePicker;

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'applied'): void;
}>();

const formatOptions = computed(() => getSelectOptions('activity_format'));
const typeOptions = computed(() => getSelectOptions('activity_type'));

const applied = ref<EventsCalendarFilterDraft>({
  activityFormat: '',
  activityType: '',
  dateRange: null,
});

const draft = reactive<{
  activityFormat: string;
  activityType: string;
  dateRange: [Dayjs, Dayjs] | null;
}>({
  activityFormat: '',
  activityType: '',
  dateRange: null,
});

const viewportWidth = ref(window.innerWidth);

const drawerWidth = computed(() => (viewportWidth.value <= 576 ? '100%' : 800));

const hasDraftValues = computed(() =>
  Boolean(draft.activityFormat || draft.activityType || draft.dateRange),
);

const hasChanges = computed(
  () =>
    draftsEqual(
      {
        activityFormat: draft.activityFormat,
        activityType: draft.activityType,
        dateRange: draft.dateRange,
      },
      applied.value,
    ) === false,
);

const isResetDisabled = computed(() => !hasDraftValues.value);
const isApplyDisabled = computed(() => !hasChanges.value);

function syncDraftFromDom(): void {
  const current = readEventsCalendarFilterDraft(getEventsCalendarScope());

  draft.activityFormat = current.activityFormat;
  draft.activityType = current.activityType;
  draft.dateRange = current.dateRange;
  applied.value = {
    activityFormat: current.activityFormat,
    activityType: current.activityType,
    dateRange: current.dateRange,
  };
}

function handleResize(): void {
  viewportWidth.value = window.innerWidth;
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    syncDraftFromDom();
  },
);

function handleReset(): void {
  draft.activityFormat = '';
  draft.activityType = '';
  draft.dateRange = null;
}

async function handleApply(): Promise<void> {
  const scope = getEventsCalendarScope();
  const nextDraft: EventsCalendarFilterDraft = {
    activityFormat: draft.activityFormat,
    activityType: draft.activityType,
    dateRange: draft.dateRange,
  };

  applyEventsCalendarFilterDraft(scope, nextDraft, { silent: true });
  await submitNewsFilters(scope, EVENTS_CALENDAR_FILTER_SUBMIT_OPTIONS);

  applied.value = nextDraft;
  emit('applied');
  emit('update:open', false);
}

onMounted(() => {
  syncDraftFromDom();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>
